import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { OrderItem, OrderService } from 'com.trumbull.phx.order.api-client';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { MultiSelectModule } from 'primeng/multiselect';
import { PanelModule } from 'primeng/panel';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import {
  FilterOptions,
  FilterSummary,
  FilterTypes,
} from './order-grid-filter.model';
import { VocabularyItemList } from 'com.trumbull.phx.vocabulary.api-client';
import { UserDefinition } from 'com.trumbull.phx.user.api-client';
import { VocabHelperService } from '../../services/vocab-helper/vocab-helper.service';
import { SharedService } from '../../services/shared.service';
// import { CleanStepPanelAccessibilityDirective } from '../../order-info/accesibility.directive';
import { DropdownFocusDirective } from '../wcag-directive/dropdownFocus.directive';

@Component({
  selector: 'app-order-grid-filter',
  imports: [
    TableModule,
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    SelectModule,
    PanelModule,
    CheckboxModule,
    ButtonModule,
    MultiSelectModule,
    // CleanStepPanelAccessibilityDirective
    DropdownFocusDirective
  ],
  templateUrl: './order-grid-filter.component.html',
  styleUrl: './order-grid-filter.component.scss',
})
export class OrderGridFilterComponent implements OnInit, OnChanges {
  @Input() ordersList: OrderItem[] = [];
  @Input() defaultOrderOwner: string = '';
  @Output() filterChange = new EventEmitter<FilterTypes>();
  @Input() orderVocab: VocabularyItemList[] = [];
  @Input() externalUser: string = '';
  @Input() users: UserDefinition[] = [];
  @Input() orderOwnerList: UserDefinition[] = [];
  @Output() emitAccountNames = new EventEmitter();
  panelCollapsed: boolean = true;
  expandedRowData: any = null;
  filteredOrdersList: OrderItem[] = [];
  @Input() ordersListFilter: FilterTypes = {
    orderOwner: [],
    accountName: [],
    createdBy: [],
    orderType: [],
    status: [],
    clientName: []
  };
  options: FilterOptions = {
    createdBy: [],
    orderType: [],
    accountName: [],
    orderOwner: [],
    clientName: []
  };
  summary = FilterSummary;
  clientNamesVocab: { code: string, label: string }[] = [];
  allAccountsClientNameList: { label: string; value: string; }[]= [];
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private readonly vocabHelperService: VocabHelperService,
    private readonly sharedService: SharedService,
    private readonly orderService: OrderService, 
  ) {}

  ngOnInit() {
    this.filteredOrdersList = [...this.ordersList];
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['ordersList'] && !changes['ordersList'].firstChange) {
      this.filteredOrdersList = [...this.ordersList];
    }
    this.options.orderType = this.fetchFilterTypes(this.orderVocab);
    if (changes['externalUser'] && this.externalUser === '')
      this.fetchAccountNames();
    
    if (changes['externalUser'] && this.externalUser) {
      this.getClientNames(this.externalUser);
    }
    
    if (changes['orderOwnerList'] && this.externalUser === '') {
      if (this.sharedService.OrderGridParams.updatedFilterValues) {
        this.onSelectAccountName();
      } else 
        this.setDefaultOrderOwnerData();
      this.generateAllSummaries();
    }

    if (changes['users']) {
      this.setCreatedByData();
      // if (this.sharedService.OrderGridParams.updatedFilterValues) {
        this.generateAllSummaries();
      // }
    }

    if ((changes['defaultOrderOwner'] && changes['defaultOrderOwner'].currentValue) || (changes['externalUser'] && this.externalUser !== '')) {
      this.summary.orderOwner =
        this.getDisplayName(this.defaultOrderOwner) || 'All Users';
      this.ordersListFilter.orderOwner =
        this.defaultOrderOwner && this.externalUser
          ? [this.defaultOrderOwner]
          : [];
    }
  }

  getDisplayName(username: string): string {
    const user = this.users.find((user) => user.principalName === username);
    return user?.displayName || '';
  }
  /**
   * Update table on filter button click
   */
  applyFilters(storeFilterValues: boolean = true) {
    const accountNames:any =
      this.ordersListFilter?.accountName ? this.ordersListFilter?.accountName.length > 0
        ? this.ordersListFilter.accountName
        : this.options.accountName : [];
    const { orderOwner, orderType, createdBy, clientName } =
      this.ordersListFilter;
    const updatedFilter = {
      ...this.ordersListFilter,
      ...(!this.externalUser && {
        createdBy: this.ordersListFilter.createdBy,
        accountName: accountNames.map((item: any) => item?.value || item)
      }), 
    };
    storeFilterValues ? this.storeAppliedFilters(updatedFilter) : '';
    this.filterChange.emit(updatedFilter);
    this.generateAllSummaries();
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        orderOwner: orderOwner.join(','),
        orderType: 'qualifiers=orderType:' + orderType.join(','),
        clientName: clientName.join(','),
        ...(!this.externalUser && {
          createdBy: createdBy?.join(','),
          account: accountNames.join(','),
        }),
      },
      queryParamsHandling: 'merge',
    });
  }

  /**
   * Reset all filter values.
   */
  resetFilters() {
    this.ordersListFilter = {
      accountName: [],
      orderOwner: [],
      orderType: [],
      createdBy: [],
      status: [],
      clientName: []
    };
    this.filteredOrdersList = [...this.ordersList];

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        orderOwner: null,
        orderType: null,
        createdBy: null,
        account: null,
        clientName: null
      },
      queryParamsHandling: 'merge',
    });
    this.defaultOrderOwner = '';
    const updatedFilter = {
      ...this.ordersListFilter,
      ...(this.externalUser ? {} : {
        accountName: this.options.accountName.map(item=> item.value),
        createdBy: this.ordersListFilter.createdBy
      }), 
    };
    this.storeAppliedFilters(updatedFilter);
    this.filterChange.emit(updatedFilter);
    this.generateAllSummaries();
    this.onSelectAccountName();
  }

  generateAllSummaries(): void {
    this.summary.orderOwner = this.getFilterSummary(
      this.ordersListFilter.orderOwner,
      'orderOwner',
      this.options.orderOwner
    );
    this.summary.orderType = this.getFilterSummary(
      this.ordersListFilter.orderType,
      'orderType',
      this.options.orderType
    );
    this.summary.clientName = this.getFilterSummary(
      this.ordersListFilter.clientName,
      'clientName',
      this.options.clientName
    );
    if (!this.externalUser) {
      this.summary.accountName = this.getFilterSummary(
        this.ordersListFilter.accountName,
        'accountName',
        this.options.accountName
      );
      this.summary.createdBy = this.getFilterSummary(
        this.ordersListFilter.createdBy,
        'createdBy',
        this.options.createdBy
      );
    }
  }

  getFilterSummary<T extends keyof FilterOptions>(
    selectedItems: any[] | undefined, 
    filterType: T,
    optionsList: { label: string; value: string }[]
  ): string {
    if (
      !selectedItems ||
      selectedItems.length === 0 ||
      selectedItems.length === (this.options[filterType] || []).length
    ) {
      return filterType === 'createdBy' || filterType === 'orderOwner'
        ? 'All Users'
        : filterType === 'clientName' ? 'All Clients' : 'All';
    }
    let displayNames: string[] = [];
    selectedItems?.forEach((item) => {
      const user = optionsList.find((option) => option.value === item);
      displayNames.push(user?.label || '')
    });
    return `${displayNames.slice(0, 2).join(', ')} ${displayNames.length > 2 ? ` +${displayNames.length - 2}` : ''}`
  }

  /**
   * Get all selected filters to show in filter summary.
   */
  getFilteredValues(
    selectedValues: { label: string; value: string }[],
    count: number
  ): string {
    return selectedValues
      .map((value) => value.label)
      .slice(0, count)
      .join(', ');
  }

  fetchFilterTypes(vocabList: any) {
    const vocabOrderData = vocabList.find((d:any) => d.vocabularyName === 'OrderType');
    if (vocabOrderData) {
      return vocabOrderData.vocabularyItems.map((item:any) => ({ value: item.code, label: item.description }));
    }
    this.generateAllSummaries();
  }

  //get all account names
  fetchAccountNames(): void {
    this.vocabHelperService.getVocabData(['UserOrganization'])
      .subscribe((data) => {
        const vocabOrgTypesData = data.find(d => d.vocabularyName === 'UserOrganization');

        if (vocabOrgTypesData) {
        this.options.accountName = (vocabOrgTypesData.vocabularyItems || []).map((item: any) => ({
          label: item.description,
          value: item.code,
        }));
        }
        // load all client names initially for all accounts
        if (!this.externalUser) {
          this.getClientNames(this.options.accountName.map(item => item.value).join(','), true);   
        }
      this.emitAccountNames.emit(this.options.accountName.map(item => item.value));
      setTimeout(() => this.applyFilters(false));
      });
  }

  //get selected order owner based on selected account
  onSelectAccountName() {
    const organization = this.externalUser
      ? [this.externalUser]
      : (this.ordersListFilter.accountName || []);
    this.options.orderOwner = this.orderOwnerList
      .filter((user: UserDefinition) => {
        if (organization.length <= 0) {
          return user?.attributes?.some(
            (attr: any) => attr.key === 'organization'
          );
        }
        return user?.attributes?.some(
          (attr: any) =>
            attr.key === 'organization' && organization.includes(attr.value)
        );
      })
      .map((user) => ({ label: user.displayName, value: user.principalName }))
      .filter(
        (item): item is { label: string; value: string } =>
          item.label !== undefined && item.value !== undefined
    );
    if (this.ordersListFilter.accountName?.length) {
      this.getClientNames(this.ordersListFilter.accountName.join(','))  
    } else {
      this.ordersListFilter.clientName = [];
      this.options.clientName = this.allAccountsClientNameList;
    }
  }

  setDefaultOrderOwnerData() {
    this.options.orderOwner = this.orderOwnerList
      .map((user) => ({ label: user.displayName, value: user.principalName }))
      .filter(
        (item): item is { label: string; value: string } =>
          item.label !== undefined && item.value !== undefined
    );
  }

  setCreatedByData() {
    this.options.createdBy = this.users
      .map((user) => ({ label: user.displayName, value: user.principalName }))
      .filter(
        (item): item is { label: string; value: string } =>
          item.label !== undefined && item.value !== undefined
    );
    // orderOwner will same as createdBy for external user
    if (this.externalUser) {
      this.options.orderOwner = this.options.createdBy;
    }
  }

  storeAppliedFilters(updatedFilter: any) {
    this.sharedService.OrderGridParams.updatedFilterValues = true;
    this.sharedService.OrderGridParams.accountName = updatedFilter?.accountName || [];
    this.sharedService.OrderGridParams.createdBy = updatedFilter?.createdBy || [];
    this.sharedService.OrderGridParams.orderOwner = updatedFilter?.orderOwner || [];
    this.sharedService.OrderGridParams.orderType = updatedFilter?.orderType || [];
    this.sharedService.OrderGridParams.clientName = updatedFilter?.clientName || [];
  }

  getClientNames(account: any, loadAllClients = false){
    this.orderService.getCustomers('standard', account).subscribe({
      next: (clients) => {
        this.vocabHelperService.getVocabData(['RecordRetrievalCustomerCode']).subscribe(res => {
          this.clientNamesVocab = ((res?.find((d: any) => d.vocabularyName === 'RecordRetrievalCustomerCode'))?.vocabularyItems)?.map((item: any) => ({ code: item.code, label: item.description })) || [];
          this.options.clientName = clients?.map((clientName: any) => {
            const matchedItem = this.clientNamesVocab?.find((item: any) => item.code === clientName.id);
            return matchedItem ? { label: matchedItem.label, value: matchedItem.code } : null;
          }).filter(item => item !== null); 
          this.options.clientName = this.sortClients(this.options.clientName);
          loadAllClients ? this.allAccountsClientNameList = this.options.clientName : '';
          //  if (this.sharedService.OrderGridParams.updatedFilterValues) {
             this.generateAllSummaries();
          //  }
        });
     },
     error: (error) => {
       console.error(error);
       },
     });
  }
  
  sortClients(list: any) {
    return list.sort((a:any, b:any) => a.label.localeCompare(b.label));
  }
}
